import {
  InputField,
  Button,
  PersonalizedQuestions,
  Spinner,
  ErrorMessage,
  Heading,
} from "@/common/components";
import { useContext, useState, useEffect } from "@/common/hooks";
import { PublicContext } from "@/routes/PublicRoute";
import {
  useChangePasswordMutation,
  useLoginQuery,
  useSetupPasswordMutation,
} from "@/service/APIService";
import { validate } from "@/utils/Validations";
import { setToken } from "@/store/authSlice";
import { checkTwoFields, encryptData } from "@/utils/Utilities";

export default function ForgotPassword() {
  const {
    labels,
    debounceState,
    debounceErrors,
    location,
    dispatch,
    navigate,
    Routes,
    uniqueId,
    skipToken,
  } = useContext(PublicContext);

  const [questions, setQuestions] = useState({});
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [fetchTokenApi, setFetchTokenApi] = useState(skipToken);

  const {
    data: token,
    isFetching,
    error: tokenError,
  } = useLoginQuery(fetchTokenApi);
  const [changePasswordApi, { isLoading, error }] = useChangePasswordMutation();
  const [setupPasswordApi, { isLoading: loading2, error: error2 }] =
    useSetupPasswordMutation();

  const checkDisabled = () => {
    return (
      Object.values(errors).some((val) => val) ||
      !password ||
      !confirmPassword ||
      (!location?.state?.password &&
        (!questions.questionOne || !questions.questionTwo))
    );
  };

  const doSaveAndLogin = async () => {
    // Setup New Password
    if (location?.state?.setupAccount) {
      setupPasswordApi({
        newPassword: await encryptData(password),
        confirmPassword: await encryptData(confirmPassword),
        customUserId: location.state.customUserId,
        operatorId: location.state.operatorId,
        answerToQuestion1: questions.questionOne?.toLowerCase(),
        answerToQuestion2: questions.questionTwo?.toLowerCase(),
        challengeQuestion1: location?.state?.challengeQuestion1,
        challengeQuestion2: location?.state?.challengeQuestion2,
      })
        .unwrap()
        .then((res) => {
          if (!res?.inError) {
            doLogin();
          }
        });
      return;
    }

    // When Password is expired
    if (location?.state?.password) {
      const payload = {
        username: location.state.username,
        password: location.state.password,
        newPassword: await encryptData(password),
        confirmPassword: await encryptData(confirmPassword),
        id: uniqueId(),
      };
      setFetchTokenApi(payload);
    }
    // Forgot Password Case
    else {
      changePasswordApi({
        password: await encryptData(password),
        confirmPassword: await encryptData(confirmPassword),
        questionOne: questions.questionOne?.toLowerCase(),
        questionTwo: questions.questionTwo?.toLowerCase(),
        customOperatorId: location.state.customUserId,
      })
        .unwrap()
        .then((res) => {
          if (!res?.inError) {
            doLogin();
          }
        });
    }
  };

  const doLogin = async () => {
    const payload = {
      username: await encryptData(location.state.customUserId),
      password: await encryptData(password),
      id: uniqueId(),
    };
    setFetchTokenApi(payload);
  };

  useEffect(() => {
    if (token && token?.access_token) {
      dispatch(setToken(token));
      navigate(Routes.dashboard.path);
    }
  }, [token]);

  useEffect(() => {
    debounceErrors(
      {
        ...errors,
        passwordMatch: checkTwoFields(password, confirmPassword, true)
          ? labels?.forgotPassword?.passwordsMustMatch
          : "",
      },
      setErrors
    );
  }, [password, confirmPassword]);

  return (
    <section className="container-medium py-5 px-3">
      {(isLoading || isFetching || loading2) && <Spinner />}
      {location.state?.password && (
        <ErrorMessage
          error={error || tokenError || error2}
          wrapperClassName="mb-3"
        />
      )}

      {!location.state?.password && (
        <>
          <Heading
            text={
              location.state?.setupAccount
                ? labels?.forgotPassword?.setupAccount
                : labels?.forgotPassword?.title
            }
            wrapperClassNames="text-center mb-5"
          />
          <ErrorMessage
            error={error || tokenError || error2}
            wrapperClassName="mb-3"
          />
          <div className="mb-3">
            <strong>{labels?.forgotPassword?.operatorId}</strong>
            {location.state.customUserId}
          </div>

          <PersonalizedQuestions
            labelOne={location?.state?.challengeQuestion1 + "*"}
            labelTwo={location?.state?.challengeQuestion2 + "*"}
            questions={questions}
            setQuestions={setQuestions}
          />
        </>
      )}

      <Heading
        text={labels?.forgotPassword?.changePassword}
        wrapperClassNames="h4 border-bottom-1 pb-2 mb-3"
        title={false}
      />
      <div className="mb-3">
        <strong>{labels?.forgotPassword?.enterPasswordTwice}</strong>
      </div>
      <InputField
        id="password"
        type="password"
        maxLength="20"
        label={labels?.forgotPassword?.newPassword}
        value={password}
        change={async (e) => {
          debounceState(e, setPassword);
          debounceErrors(
            {
              ...errors,
              password7: await validate(e, "password7"),
            },
            setErrors
          );
        }}
        touch={() => setTouched({ ...touched, password: true })}
        touched={touched.password}
        error={errors.password7}
      />

      <InputField
        id="confirmPassword"
        type="password"
        maxLength="20"
        label={labels?.forgotPassword?.confirmPassword}
        value={confirmPassword}
        change={async (e) => {
          debounceState(e, setConfirmPassword);
          debounceErrors(
            {
              ...errors,
              confirmPassword: await validate(e, "confirmPassword"),
            },
            setErrors
          );
        }}
        touch={() => setTouched({ ...touched, confirmPassword: true })}
        touched={touched.confirmPassword}
        error={errors.confirmPassword || errors.passwordMatch}
      />

      <div className="d-flex justify-content-end mt-5">
        <Button
          id="cancel"
          wrapperClassName="col-md-4 me-2"
          text={labels?.forgotPassword?.cancel}
          click={() => navigate(Routes.login.path)}
          type="secondary"
        />
        <Button
          id="save"
          text={labels?.forgotPassword?.save}
          wrapperClassName="col-md-4"
          click={doSaveAndLogin}
          disabled={checkDisabled()}
        />
      </div>
    </section>
  );
}
